import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Users, ClipboardList, BookOpen, Package, ArrowRight, AlertTriangle, Shield, Trophy, BarChart3 } from "lucide-react";

import { API_URL as API } from "../../config";

export default function DashboardPage() {
  const { authFetch } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const a = t.admin;

  useEffect(() => {
    const load = async () => {
      const res = await authFetch(`${API}/api/admin/dashboard`);
      if (res.ok) setStats(await res.json());
    };
    load();
  }, [authFetch]);

  const cards = stats ? [
    { label: a.totalAthletes, value: stats.athletes.total, sub: `${stats.athletes.active} ${a.active}`, icon: Users, color: "bg-blue-500", to: "/admin/athletes" },
    { label: a.todaysAttendance, value: stats.attendance.today_total > 0 ? `${Math.round((stats.attendance.today_present / stats.attendance.today_total) * 100)}%` : "N/A", sub: `${stats.attendance.today_present}/${stats.attendance.today_total} ${a.present}`, icon: ClipboardList, color: "bg-emerald-500", to: "/admin/attendance" },
    { label: a.academicRecords, value: stats.academics.total_records, sub: a.totalEntries, icon: BookOpen, color: "bg-violet-500", to: "/admin/academics" },
    { label: a.gearInventory, value: stats.gear.total, sub: stats.gear.needs_replacement > 0 ? `${stats.gear.needs_replacement} ${a.needReplacement}` : a.allGoodShape, icon: Package, color: stats.gear.needs_replacement > 0 ? "bg-amber-500" : "bg-teal-500", to: "/admin/gear" },
    { label: a.teams, value: stats.teams.total, sub: a.createdTeams, icon: Shield, color: "bg-sky-500", to: "/admin/teams" },
    { label: a.tournaments, value: stats.tournaments.total, sub: a.organizedEvents, icon: Trophy, color: "bg-orange-500", to: "/admin/tournaments" },
    { label: a.siteVisitors, value: stats.analytics.total_visits, sub: a.totalPageViews, icon: BarChart3, color: "bg-indigo-500", to: "/admin/analytics" },
  ] : [];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 font-['Kanit']">{a.programOverview}</h2>
        <p className="text-gray-500 mt-1">{a.atAGlance}</p>
      </div>
      {!stats ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-36 bg-white rounded-2xl animate-pulse" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {cards.map(card => (
              <Link key={card.label} to={card.to} data-testid={`dash-card-${card.label.toLowerCase().replace(/\s/g, '-')}`}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all group">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 ${card.color} rounded-xl flex items-center justify-center`}>
                    <card.icon className="w-6 h-6 text-white" />
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#87CEEB] transition-colors" />
                </div>
                <p className="text-3xl font-bold text-gray-900 font-['Kanit']">{card.value}</p>
                <p className="text-sm text-gray-500 mt-1">{card.label}</p>
                <p className="text-xs text-gray-400 mt-0.5">{card.sub}</p>
              </Link>
            ))}
          </div>
          {stats.gear.needs_replacement > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-center gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-800">{stats.gear.needs_replacement} {a.gearAlert}</p>
                <Link to="/admin/gear" className="text-sm text-amber-600 hover:underline">{a.viewInventory}</Link>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
